import React, { useRef, useCallback } from "react";
import LightGallery from "lightgallery/react";
import lgZoom from "lightgallery/plugins/zoom";
import lgVideo from "lightgallery/plugins/video";
import { GalleryContainer } from "../styles";
import { WorkProjectShell } from "./WorkProjectShell";
import {
  WorkSplitRow,
  WorkMediaFrame,
  WorkTextStack,
  WorkSectionHeading,
  StyledImageGrid,
  ConcludeContainer,
  WorkGallerySection,
} from "./styles";
import { ProjectDetails } from "../../../DesignSystem/ProjectDetails";

export default function RobotSimulation() {
  const ProjectBG = require("../../../../assets/videos/robot-simulation/poster.svg");
  const R1 = require("../../../../assets/videos/robot-simulation/r1.png");
  const R2 = require("../../../../assets/videos/robot-simulation/r2.png");
  const R3 = require("../../../../assets/videos/robot-simulation/r3.png");
  const R4 = require("../../../../assets/videos/robot-simulation/r4.png");
  const R5 = require("../../../../assets/videos/robot-simulation/r5.png");
  const Grid1 = require("../../../../assets/videos/robot-simulation/grid1.svg");
  const Grid2 = require("../../../../assets/videos/robot-simulation/grid2.png");
  const Grid3 = require("../../../../assets/videos/robot-simulation/grid3.png");

  const lightGallery = useRef<any>(null);
  const items = [
    { id: "1", size: "1400-933", src: R3, thumb: R3, subHtml: `` },
    { id: "2", size: "1400-933", src: R5, thumb: R5, subHtml: `` },
    { id: "3", size: "1400-932", src: R1, thumb: R1, subHtml: `` },
    { id: "4", size: "1400-932", src: R4, thumb: R4, subHtml: `` },
    { id: "5", size: "1400-932", src: R2, thumb: R2, subHtml: `` },
  ];

  const onInit = useCallback((detail: any) => {
    if (detail) lightGallery.current = detail.instance;
  }, []);

  const getItems = useCallback(() => {
    return items.map((item) => (
      <a
        key={item.id}
        data-lg-size={item.size}
        className="gallery-item"
        data-src={item.src}
        href={item.src}
      >
        <img
          className="img-responsive w-100 rounded"
          src={item.thumb}
          alt="Robot simulation gallery preview"
        />
      </a>
    ));
  }, [items]);

  return (
    <WorkProjectShell
      posterSrc={ProjectBG}
      title="Robot simulation in VR & AR"
      lead={
        <p>
          VR Robot Control is an immersive learning experience for HTC Vive: a
          3D-printed controller and Bluetooth link teach manual robot
          interaction in a safe virtual space.
        </p>
      }
    >
      <WorkSplitRow>
        <WorkMediaFrame>
          <StyledImageGrid src={Grid1} alt="" />
        </WorkMediaFrame>
        <WorkTextStack>
          <WorkSectionHeading>Immersive VR environment</WorkSectionHeading>
          <p>
            Practice robot movements and actions in a controlled virtual world
            built for visualization and understanding.
          </p>
          <WorkSectionHeading>3D-printed controller</WorkSectionHeading>
          <p>
            Physical buttons and joysticks mirror real controls for tactile,
            hands-on training.
          </p>
        </WorkTextStack>
      </WorkSplitRow>

      <WorkSplitRow $reverse>
        <WorkMediaFrame>
          <StyledImageGrid src={Grid2} alt="" />
        </WorkMediaFrame>
        <WorkTextStack>
          <WorkSectionHeading>Bluetooth communication</WorkSectionHeading>
          <p>
            Wireless link between the printed controller and the simulation for
            responsive control.
          </p>
          <WorkSectionHeading>Three learning stages</WorkSectionHeading>
          <p>
            Drilling, pick-and-place, and welding scenarios build skill through
            progressive challenges.
          </p>
        </WorkTextStack>
      </WorkSplitRow>

      <WorkSplitRow>
        <WorkMediaFrame>
          <StyledImageGrid src={Grid3} alt="" />
        </WorkMediaFrame>
        <WorkTextStack>
          <WorkSectionHeading>Drilling</WorkSectionHeading>
          <p>Drill a hole in a target object with precision and control.</p>
          <WorkSectionHeading>Pick and place</WorkSectionHeading>
          <p>Move objects with the arm to a designated location.</p>
          <WorkSectionHeading>Welding</WorkSectionHeading>
          <p>Weld two objects together using guided robotic technique.</p>
        </WorkTextStack>
      </WorkSplitRow>

      <ConcludeContainer>
        <p>
          The project combines a physical controller, Bluetooth, and VR so
          learners gain practical familiarity with robotics in an engaging
          environment.
        </p>
      </ConcludeContainer>

      <ProjectDetails
        details={[
          {
            label: "Project Name",
            value: "Virtual Reality Robot training application (Prototype)",
          },
          { label: "Period", value: "04.2018 - 08.2018" },
          { label: "Client", value: "Volkswagen" },
          { label: "Engine & Tools", value: "Unity3D, Arduino, C#" },
          { label: "Platform", value: "VR (HTC Vive)" },
          { label: "Company", value: "UReality" },
          { label: "Tags", value: "VR" },
        ]}
      />

      <WorkGallerySection>
        <GalleryContainer>
          <LightGallery
            plugins={[lgVideo, lgZoom]}
            elementClassNames="gallery-custom-wrapper row flex-column flex-sm-row"
            onInit={onInit}
            data-lg-size={2}
          >
            {getItems()}
          </LightGallery>
        </GalleryContainer>
      </WorkGallerySection>
    </WorkProjectShell>
  );
}
